import { Box, Button, Link as ChakraLink, Flex, Text, VStack } from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";

import { AuthService } from "src/rest/authService";
import { EmailInput } from "src/common/emailInput";
import { EnglishLabels } from "src/common/constants/englishLabels";
import { GlobalUtils } from "src/utils/globalUtils";
import { HindiLabels } from "src/common/constants/hindiLabels";
import { PasswordInput } from "src/common/passwordInput";
import { QuestionOutlineIcon } from "@chakra-ui/icons";
import { RootState } from "src/store/store";
import { UseAppToast } from "src/common/useAppToast";
import { useSelector } from "react-redux";
import { useState } from "react";

const SUPPORT_EMAIL_LINK = "mailto:contact@mauryasamaj.in?cc=contactmauryasamaj@gmail.com";

export const ForgotPassword: React.FC = () => {
    const [emailId, setEmailId] = useState("");
    const [loading, setLoading] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [tempPassword, setTempPassword] = useState("");
    const [isEmailValidated, setIsEmailValidated] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isNewPasswordTouched, setIsNewPasswordTouched] = useState(false);
    const [isConfirmNewPasswordTouched, setIsConfirmNewPasswordTouched] = useState(false);
    const navigate = useNavigate();

    const isNewPasswordValid = GlobalUtils.isValidPassword(newPassword);
    const isConfirmNewPasswordValid = GlobalUtils.isValidPassword(confirmNewPassword) && confirmNewPassword === newPassword;

    const { errorToast, successToast } = UseAppToast();
    const globalInfo = useSelector((state: RootState) => state.globalInfo);
    const { globalLanguage } = globalInfo;
    const isHindiLabels = globalLanguage === "HI";

    const handleVerifyEmail = async () => {
        try {
            setLoading(true);
            const response = await AuthService.verifyEmail(emailId);
            if (response.status === 200) {
            successToast(isHindiLabels ? HindiLabels.Reset_Link_Sent : EnglishLabels.Reset_Link_Sent);
            setIsEmailValidated(true)
            } else {
            errorToast(isHindiLabels ? HindiLabels.Failed_To_Send_Reset_Link : EnglishLabels.Failed_To_Send_Reset_Link);
            setIsEmailValidated(true)
            }
            setLoading(false);
        } catch (error) {
            console.error("Error sending reset link:", error);
            errorToast(isHindiLabels ? HindiLabels.Failed_To_Send_Reset_Link : EnglishLabels.Failed_To_Send_Reset_Link);
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
      try {
        // ensure password fields are not empty
        if (!tempPassword || !tempPassword.trim() || !newPassword || !newPassword.trim() || !confirmNewPassword || !confirmNewPassword.trim()) {
          errorToast(isHindiLabels ? "कृपया सभी पासवर्ड फ़ील्ड भरें" : "Please fill in all password fields!");
          return;
        }

        if (!GlobalUtils.isValidPassword(newPassword)) {
          errorToast(isHindiLabels ? HindiLabels.PASSWORD_REQUIREMENTS_HINT : EnglishLabels.PASSWORD_REQUIREMENTS_HINT);
          return;
        }

        if(newPassword !== confirmNewPassword) {
          errorToast(isHindiLabels ? "नया पासवर्ड मेल नहीं खाते" : "New passwords do not match!");
          return;
        }
        setLoading(true);
        const response = await AuthService.resetPassword(emailId, newPassword, tempPassword);
        if (response.status === 201) {
          successToast(isHindiLabels ? HindiLabels.RESET_PASSWORD_SUCCESS : EnglishLabels.RESET_PASSWORD_SUCCESS);
          navigate("/login");
        } else {
          errorToast(isHindiLabels ? HindiLabels.FAILED_TO_RESET_PASSWORD : EnglishLabels.FAILED_TO_RESET_PASSWORD);
        }
      } catch (error: any) {
        console.error("Error resetting password:", error);
        errorToast(isHindiLabels ? HindiLabels.FAILED_TO_RESET_PASSWORD : EnglishLabels.FAILED_TO_RESET_PASSWORD);
      } finally {
        setLoading(false);
      }
    };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="#fff">
      <Box w={{ base: "85%", sm: "500px", md: "600px" }} maxW="95vw" p={{ base: 4, sm: 8 }} borderRadius="lg" boxShadow="none">
        <Text
          fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
          fontWeight="bold"
          mb={2}
          textAlign="center"
          color="#2D1A17"
          lineHeight={1.1}
          whiteSpace="normal"
        >
          {isHindiLabels ? HindiLabels.Trouble_Logging_In : EnglishLabels.Trouble_Logging_In}
        </Text>
        <Text fontSize="large" color="gray.600" mb={8} textAlign="center">
          {isHindiLabels ? HindiLabels.Enter_Email_Address_Description : EnglishLabels.Enter_Email_Address_Description}
        </Text>
        <VStack mx={2} spacing={4} alignItems="center">
          <EmailInput
            onChange={setEmailId}
            isRequired={true}
            value={emailId}
            isHindiLabels={isHindiLabels}
            setIsEmailValid={setIsEmailValid}
          />
          {isEmailValidated && (
            <VStack width="100%" mx={2} spacing={4} alignItems="center">
              <Text fontSize={{base: "sm", md: "md"}} color="gray.600" textAlign="center">
                {isHindiLabels ? HindiLabels.TEMP_PASSWORD_MESSAGE : EnglishLabels.TEMP_PASSWORD_MESSAGE}
              </Text>
              <PasswordInput
                setPassword={setTempPassword}
                placeholder={
                isHindiLabels ? HindiLabels.ENTER_TEMP_PASSWORD : "Enter Your Temporary Password"
                }
              />
              <VStack width="100%" alignItems="start" spacing="0.25rem">
                <PasswordInput
                  setPassword={setNewPassword}
                  isInvalid={isNewPasswordTouched && !isNewPasswordValid}
                  onBlur={() => setIsNewPasswordTouched(true)}
                  placeholder={
                  isHindiLabels ? HindiLabels.ENTER_NEW_PASSWORD : "Enter New Password"
                  }
                />
                {isNewPasswordTouched && !isNewPasswordValid && (
                  <Text color="#E53E3E" fontSize="sm">
                    {isHindiLabels ? HindiLabels.PASSWORD_REQUIREMENTS_HINT : EnglishLabels.PASSWORD_REQUIREMENTS_HINT}
                  </Text>
                )}
              </VStack>
              <VStack width="100%" alignItems="start" spacing="0.25rem">
                <PasswordInput
                  setPassword={setConfirmNewPassword}
                  isInvalid={isConfirmNewPasswordTouched && !isConfirmNewPasswordValid}
                  onBlur={() => setIsConfirmNewPasswordTouched(true)}
                  placeholder={
                  isHindiLabels ? HindiLabels.CONFIRM_NEW_PASSWORD : "Confirm New Password"
                  }
                />
                {isConfirmNewPasswordTouched && !isConfirmNewPasswordValid && (
                  <Text color="#E53E3E" fontSize="sm">
                    {confirmNewPassword !== newPassword
                      ? (isHindiLabels ? "नया पासवर्ड मेल नहीं खाते" : "New passwords do not match!")
                      : (isHindiLabels ? HindiLabels.PASSWORD_REQUIREMENTS_HINT : EnglishLabels.PASSWORD_REQUIREMENTS_HINT)}
                  </Text>
                )}
              </VStack>
            </VStack>
          )}
          <Button
            hidden={isEmailValidated}
            isDisabled={!isEmailValid}
            width="100%"
            variant="mks"
            alignItems="center"
            onClick={handleVerifyEmail}
            isLoading={loading}
          >
            {isHindiLabels ? HindiLabels.VERIFY_EMAIL : EnglishLabels.VERIFY_EMAIL}
          </Button>
          <Button
            hidden={!isEmailValidated}
            isDisabled={!isNewPasswordValid || !isConfirmNewPasswordValid}
            width="100%"
            variant="mks"
            alignItems="center"
            onClick={handleResetPassword}
            isLoading={loading}
          >
            {isHindiLabels ? HindiLabels.RESET_PASSWORD : EnglishLabels.RESET_PASSWORD}
          </Button>
        </VStack>
        <Box textAlign="center" mt={6} mb={2}>
          <Text fontSize="sm" color="gray.500" alignItems="center" display="flex" justifyContent="center" gap={1}>
            <QuestionOutlineIcon alignSelf="center" />
            {isHindiLabels ? HindiLabels.Need_Help : EnglishLabels.Need_Help}{' '}
            <ChakraLink href={SUPPORT_EMAIL_LINK} color="#FF9900" fontWeight="medium" fontSize="md">
              {isHindiLabels ? HindiLabels.Contact_Support : EnglishLabels.Contact_Support}
            </ChakraLink>
          </Text>
        </Box>
        <Box textAlign="center" mt={2}>
          <ChakraLink as={Link} to="/login" color="#2D1A17" fontWeight="semibold" fontSize="md">
            {isHindiLabels ? HindiLabels.Back_To_Login : EnglishLabels.Back_To_Login}
          </ChakraLink>
        </Box>
      </Box>
    </Flex>
  );
};